/**
 * Public RUM site tokens.
 *
 * Cloudflare-Web-Analytics-style: a site token is a PUBLIC routing key
 * embedded in the page (visible in page source and network requests by
 * design), NOT a secret. It only decides which environment an
 * unauthenticated flush is stamped with — identity, geo, and ingress fields
 * are all server-derived at the edge, and client identity claims are only
 * honoured for the allowlisted baggage keys, so a spoofed token can at worst
 * pollute one bounded environment's series with noise. The ingest worker
 * additionally rate-limits token-lane posts per token + client IP.
 *
 * Static sites (marketing, docs) cannot mint a Flux session token — they
 * deliberately ship (almost) no client JavaScript — so their snippet posts
 * with only the site token. The main app sends its site token alongside its
 * Bearer token, which keeps the lane uniform and lets pre-auth page loads
 * (before the Flux token resolves) report RUM instead of dropping the batch.
 */

/** Query parameter carrying the site token on POST /telemetry/perf. */
export const RUM_SITE_TOKEN_QUERY_PARAM = "st";

export interface RumSiteTokenEntry {
    /** Human-readable site name, for logs and registry hygiene. */
    readonly site: string;
    /**
     * Environment stamped on token-lane events. `null` means "derive from
     * the ingest hostname" — used by the app token, whose environment
     * (production / pr-N preview) is already known server-side.
     */
    readonly environment: string | null;
}

/**
 * The closed token registry. Tokens are opaque random suffixes so a token
 * identifies exactly one site even if sites are renamed; adding a site means
 * adding a row here (bounded environments — never accept a client-supplied
 * environment name on the token lane).
 */
export const RUM_SITE_TOKENS: Readonly<Record<string, RumSiteTokenEntry>> = {
    rum_pk_app_9f2c41d7a8b3: {site: "app", environment: null},
    rum_pk_marketing_5e8a03c6f1d9: {site: "marketing", environment: "marketing"},
    rum_pk_docs_2b7f94e0c35a: {site: "docs", environment: "docs"},
    rum_pk_search_7d4d72cf5a3e: {site: "search", environment: "search"},
};

/** The app's own site token, sent by the frontend alongside its Bearer auth. */
export const RUM_APP_SITE_TOKEN = "rum_pk_app_9f2c41d7a8b3";

/** The marketing site's token, embedded in the static-site RUM snippet. */
export const RUM_MARKETING_SITE_TOKEN = "rum_pk_marketing_5e8a03c6f1d9";

/** The docs site's token. */
export const RUM_DOCS_SITE_TOKEN = "rum_pk_docs_2b7f94e0c35a";

/** The standalone site-search pages' token (/search, /sitemap). */
export const RUM_SEARCH_SITE_TOKEN = "rum_pk_search_7d4d72cf5a3e";

export function getRumSiteTokenEntry(token: string | null | undefined): RumSiteTokenEntry | undefined {
    if (token === null || token === undefined || token === "") return undefined;
    return Object.prototype.hasOwnProperty.call(RUM_SITE_TOKENS, token) ? RUM_SITE_TOKENS[token] : undefined;
}
